import React, { useMemo, useState } from 'react';
import { Button, Collapse, Dropdown, Tag } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { RouteFlowProjection } from '../../routes/execution/dashboardProjection';
import type { OperationalActionDescriptor } from '../../routes/execution/executionActionsFacade';
import type { WorkspaceComposition } from './workspaceComposition';
import type { SharedUnloadHint } from './dispatcherContextProjection';
import { formatSharedUnloadPresentation } from '../../routes/routeSummaryProjection';
import { quickActionButtonLabel } from './dispatcherContextProjection';
import { formatOperationalDateTime } from '../../routes/execution/operationalContextFacade';
import ExecutionActionModal, {
  type ExecutionActionSubmitPayload
} from '../ExecutionActionModal';
import type { ExecutionCargoHydration } from '../types';
import { cargoAtStop } from '../executionHelpers';

type Props = {
  routeId: number;
  routeFlow: RouteFlowProjection;
  composition: WorkspaceComposition;
  sharedUnloadHints: Map<string, SharedUnloadHint>;
  stopQuickActions: Map<string, OperationalActionDescriptor>;
  hydrationCargo: ExecutionCargoHydration[];
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  actionLoading?: boolean;
};

function flowIcon(stop: RouteFlowProjection['stops'][0], overdue: boolean): string {
  if (stop.isCompleted) return '✅';
  if (stop.isActive) return '🔵';
  if (overdue) return '🔴';
  return '⚪';
}

export const RouteFlowCompact: React.FC<Props> = ({
  routeId,
  routeFlow,
  composition,
  sharedUnloadHints,
  stopQuickActions,
  hydrationCargo,
  onSubmit,
  actionLoading = false
}) => {
  const overdueSet = new Set(composition.attention.overdueStopUids);
  const [activeDescriptor, setActiveDescriptor] = useState<OperationalActionDescriptor | null>(null);

  const scrollable = routeFlow.stops.length >= 5;

  const cargoAtStopForModal = useMemo(() => {
    if (!activeDescriptor?.executionStopUid) return [];
    const stop = routeFlow.stops.find((s) => s.executionStopUid === activeDescriptor.executionStopUid);
    if (!stop) return [];
    return cargoAtStop(hydrationCargo, activeDescriptor.executionStopUid, stop.type);
  }, [activeDescriptor, hydrationCargo, routeFlow.stops]);

  const runQuick = (descriptor: OperationalActionDescriptor) => {
    if (descriptor.disabled) return;
    setActiveDescriptor(descriptor);
  };

  const items = routeFlow.stops.map((stop) => {
    const overdue = overdueSet.has(stop.executionStopUid);
    const icon = flowIcon(stop, overdue);
    const shared = sharedUnloadHints.get(stop.executionStopUid);
    const quick = stopQuickActions.get(stop.executionStopUid);

    const extraMenu = quick
      ? [
          {
            key: 'quick',
            label: quick.label,
            onClick: () => runQuick(quick)
          }
        ]
      : [];

    return {
      key: stop.executionStopUid,
      label: (
        <div className="execution-workspace__flow-item" style={{ border: 'none', padding: 0 }}>
          <span className="execution-workspace__flow-icon">{icon}</span>
          <div className="execution-workspace__flow-body">
            <div className="execution-workspace__flow-line">
              {stop.label}
              {overdue ? ' · просрочено' : ''}
              {shared ? (
                <Tag color="purple" style={{ marginLeft: 6, fontSize: 10 }}>
                  Общая выгрузка
                </Tag>
              ) : null}
            </div>
            {stop.destinationLabel ? (
              <div className="execution-workspace__flow-sub" style={{ fontWeight: 500 }}>
                {stop.destinationLabel}
              </div>
            ) : null}
            <div className="execution-workspace__flow-sub">{stop.shortCargoSummary}</div>
            {shared ? (
              <div className="execution-workspace__flow-shared">
                {formatSharedUnloadPresentation({
                  cargoCount: shared.cargoCount,
                  destinationLabel: shared.destinationLabel
                })}
              </div>
            ) : null}
          </div>
          <div className="execution-workspace__flow-actions" onClick={(e) => e.stopPropagation()}>
            {quick && !stop.isCompleted ? (
              <Button
                type="link"
                size="small"
                style={{ padding: '0 4px', height: 'auto', fontSize: 11 }}
                onClick={() => runQuick(quick)}
              >
                {quickActionButtonLabel(quick)}
              </Button>
            ) : null}
            {extraMenu.length > 0 ? (
              <Dropdown menu={{ items: extraMenu }} trigger={['click']}>
                <Button type="text" size="small" icon={<MoreOutlined />} />
              </Dropdown>
            ) : null}
          </div>
        </div>
      ),
      children: (
        <div style={{ fontSize: 11, paddingLeft: 26 }}>
          <div>{stop.address ?? '—'}</div>
          {stop.clientName ? <div>Клиент: {stop.clientName}</div> : null}
          {stop.consigneeName ? <div>Грузополучатель: {stop.consigneeName}</div> : null}
          {stop.applicationNumber ? <div>Заявка №{stop.applicationNumber}</div> : null}
          <div>
            План: {formatOperationalDateTime(stop.plannedAt)} · Факт:{' '}
            {formatOperationalDateTime(stop.actualAt)}
          </div>
          <div className="execution-workspace__muted">{stop.stateLabel}</div>
        </div>
      )
    };
  });

  return (
    <section className="execution-workspace__section">
      <div className="execution-workspace__section-title">
        Маршрут {routeFlow.stops.length > 0 ? `(${routeFlow.stops.length})` : ''}
      </div>
      <div className={scrollable ? 'execution-workspace__flow-scroll' : undefined}>
        <Collapse items={items} size="small" bordered={false} expandIconPosition="end" />
      </div>
      <ExecutionActionModal
        open={activeDescriptor != null}
        descriptor={activeDescriptor}
        routeId={routeId}
        cargoAtStop={cargoAtStopForModal}
        loading={actionLoading}
        onCancel={() => setActiveDescriptor(null)}
        onSubmit={async (payload) => {
          await onSubmit(payload);
          setActiveDescriptor(null);
        }}
      />
    </section>
  );
};

export default RouteFlowCompact;
